import React, { useState } from 'react';
import { Plus, Minus } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

/**
 * Reusable FAQ section with FAQPage JSON-LD for SEO + AI search citations.
 *
 *   <FAQ
 *     eyebrow="FREQUENTLY ASKED"
 *     line1="Everything you wanted"
 *     line2Italic="to ask first."
 *     items={[
 *       { q: "What is X?",   a: "X is …" },
 *       { q: "Does X do Y?", a: "Yes — …" },
 *     ]}
 *   />
 *
 * Inject the FAQPage schema only once per page even if multiple FAQs are
 * rendered (Google flags duplicate FAQPage as a structured-data warning).
 * Pass `omitSchema` on any FAQ instance after the first one on the page.
 */
export default function FAQ({
  eyebrow = 'FREQUENTLY ASKED',
  line1 = 'Common',
  line2Italic = 'questions.',
  items = [],
  omitSchema = false,
}) {
  const [openIdx, setOpenIdx] = useState(0);

  return (
    // Bottom padding intentionally smaller than top — the section
    // that follows the FAQ on every marketing page is a CTA with its
    // own generous top padding. The previous `py-20 md:py-28`
    // stacked with that CTA padding produced ~200px of dead space
    // between the last Q&A and the next section. Tighter bottom
    // keeps the rhythm without making the FAQ feel cramped.
    <section className="pt-20 md:pt-28 pb-10 md:pb-14" style={{ background: 'var(--paper)' }}>
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-14 tk-reveal">
          <div className="inline-flex items-center gap-2 mb-4">
            <span className="tk-dot" />
            <span className="tk-eyebrow">{eyebrow}</span>
          </div>
          <h2 className="tk-display mx-auto" style={{ fontSize: 'clamp(28px, 3.6vw, 44px)', maxWidth: 720 }}>
            {line1}{' '}<span className="tk-italic">{line2Italic}</span>
          </h2>
        </div>

        <div className="tk-reveal" style={{ borderTop: '1px solid var(--paper-line)' }}>
          {items.map((it, i) => {
            const isOpen = openIdx === i;
            return (
              <div
                key={i}
                style={{ borderBottom: '1px solid var(--paper-line)' }}
              >
                <button
                  type="button"
                  onClick={() => setOpenIdx(isOpen ? -1 : i)}
                  aria-expanded={isOpen}
                  className="w-full flex items-start gap-4 py-5 text-left transition-colors"
                  style={{ background: 'transparent' }}
                >
                  <span
                    className="inline-flex items-center justify-center shrink-0 mt-0.5"
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: 8,
                      background: isOpen ? 'var(--mint-700)' : 'var(--paper-2)',
                      color: isOpen ? 'var(--paper)' : 'var(--ink-2)',
                      border: '1px solid var(--paper-line)',
                      transition: 'background .15s, color .15s',
                    }}
                  >
                    {isOpen ? <Minus size={14} /> : <Plus size={14} />}
                  </span>
                  <span
                    className="tk-display flex-1"
                    style={{ fontSize: 17, lineHeight: 1.35, color: 'var(--ink)' }}
                  >
                    {it.q}
                  </span>
                </button>

                {isOpen && (
                  <div
                    className="pb-5 pl-10 pr-2"
                    style={{ color: 'var(--ink-2)', fontSize: 14.5, lineHeight: 1.7 }}
                  >
                    {it.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {!omitSchema && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: items.map(({ q, a }) => ({
                '@type': 'Question',
                name: q,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: typeof a === 'string' ? a : '',
                },
              })),
            })}
          </script>
        </Helmet>
      )}
    </section>
  );
}
